import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Personal Care Assistance",
  "Senior Home Care",
  "Companionship",
  "Medication Reminders",
  "Post-Surgery Care",
  "Dementia & Alzheimer’s Support",
  "Meal Preparation",
  "Mobility Assistance",
  "Respite Care for Families",
  "Light Housekeeping",
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: date ? date.toISOString().slice(0, 10) : null,
      });
      setDone(true);
      toast.success("Booking received. Our care team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", notes: "" });
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 md:py-32 bg-[#EEF4F8]" data-testid="booking-section">
      <div className="container-pad mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-5">
            Book a Consultation
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F2942] leading-[1.1]" data-testid="booking-heading">
            Let’s build a care plan, together.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed max-w-md">
            Tell us a little about your needs. A care coordinator will call
            within 24 hours to schedule a free, no-obligation consultation.
          </p>

          <ul className="mt-8 space-y-3 text-[#0F2942]">
            {[
              "Free 20-minute consultation",
              "Personalised care plan",
              "Care can begin within 24 to 72 hours",
              "PIPEDA-conscious & confidential",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#7FB3D5] mt-0.5" />
                <span className="text-[#475569]">{line}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7 bg-[#F8FAFC] rounded-3xl p-6 md:p-10 border border-[#1E5F8C]/10 soft-shadow"
          data-testid="booking-form"
        >
          {done ? (
            <div className="text-center py-12" data-testid="booking-success">
              <div className="h-14 w-14 rounded-full bg-[#DCEAF2] flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} className="text-[#1E5F8C]" />
              </div>
              <h3 className="font-serif text-2xl mt-5 text-[#0F2942]">Thank you</h3>
              <p className="mt-3 text-[#475569] max-w-md mx-auto">
                Your request has been received. A Julicare care coordinator will
                reach out within 24 hours to schedule your free consultation.
              </p>
              <button
                type="button"
                onClick={() => setDone(false)}
                className="mt-6 text-sm font-medium text-[#1E5F8C] underline-offset-4 hover:underline"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <input
                  data-testid="booking-name"
                  required
                  value={form.name}
                  onChange={handle("name")}
                  className="w-full bg-[#EEF4F8] border-none rounded-xl px-4 py-3 text-[#0F2942] focus:ring-sage focus:outline-none"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email *">
                <input
                  data-testid="booking-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={handle("email")}
                  className="w-full bg-[#EEF4F8] border-none rounded-xl px-4 py-3 text-[#0F2942] focus:ring-sage focus:outline-none"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Phone *">
                <input
                  data-testid="booking-phone"
                  required
                  value={form.phone}
                  onChange={handle("phone")}
                  className="w-full bg-[#EEF4F8] border-none rounded-xl px-4 py-3 text-[#0F2942] focus:ring-sage focus:outline-none"
                  placeholder="647-804-0263"
                />
              </Field>

              <Field label="Service needed *">
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm({ ...form, service: v })}
                >
                  <SelectTrigger
                    data-testid="booking-service"
                    className="w-full bg-[#EEF4F8] border-none rounded-xl px-4 py-3 h-auto text-[#0F2942] focus:ring-sage"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`booking-service-opt-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Preferred start date">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className="w-full flex items-center justify-between bg-[#EEF4F8] rounded-xl px-4 py-3 text-left text-[#0F2942]"
                    >
                      <span className={date ? "" : "text-[#475569]"}>
                        {date ? format(date, "PPP") : "Pick a date"}
                      </span>
                      <CalendarIcon size={18} className="text-[#475569]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Anything we should know?" className="sm:col-span-2">
                <textarea
                  data-testid="booking-notes"
                  rows={4}
                  value={form.notes}
                  onChange={handle("notes")}
                  className="w-full bg-[#EEF4F8] border-none rounded-xl px-4 py-3 text-[#0F2942] focus:ring-sage focus:outline-none resize-none"
                  placeholder="Briefly describe the care you’re looking for, any conditions, or scheduling preferences."
                />
              </Field>

              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-full bg-[#1E5F8C] text-[#F8FAFC] px-8 py-4 font-medium hover:bg-[#2A75AB] transition-colors disabled:opacity-60"
                  data-testid="booking-submit"
                >
                  {submitting ? "Sending..." : "Request Consultation"}
                </button>
                <p className="text-xs text-[#475569]">
                  By submitting you agree to be contacted by our care team. We
                  never share your information.
                </p>
              </div>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-[#0F2942] mb-2">{label}</span>
      {children}
    </label>
  );
}
